import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type TooltipItem,
} from 'chart.js';
import React from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export interface IChartDataPoint {
  date: string;
  value: number;
}

export interface IChartDataset {
  label: string;
  data: IChartDataPoint[];
  color: string;
  count: number;
}

export interface IStackedBarChartProps {
  title: string;
  subtitle?: React.ReactNode;
  datasets: IChartDataset[];
  summary?: {
    period: React.ReactNode;
    totalChanges: number;
  };
  height?: string;
  className?: string;
  barThickness?: number;
  maxBarThickness?: number;
  barPercentage?: number;
  categoryPercentage?: number;
}

const StackedBarChart: React.FC<IStackedBarChartProps> = ({
  title,
  subtitle,
  datasets,
  summary,
  height = '400px',
  className = '',
  barThickness,
  maxBarThickness,
  barPercentage,
  categoryPercentage,
}) => {
  // Transform data for Chart.js
  const labels = datasets[0]?.data.map((point) => point.date) || [];

  const chartDatasets = datasets.map((dataset) => ({
    label: dataset.label,
    data: dataset.data.map((point) => point.value),
    backgroundColor: dataset.color,
    borderColor: dataset.color,
    borderWidth: 1,
    stack: 'Stack 0', // This makes it a stacked bar chart
    ...(barThickness !== undefined ? { barThickness } : {}),
    ...(maxBarThickness !== undefined ? { maxBarThickness } : {}),
    ...(barPercentage !== undefined ? { barPercentage } : {}),
    ...(categoryPercentage !== undefined ? { categoryPercentage } : {}),
  }));

  const formatYAxisTick = (value: number) => {
    if (!Number.isFinite(value)) {
      return value as unknown as string;
    }

    const absValue = Math.abs(value);
    if (absValue >= 1_000_000_000) {
      const formatted = absValue / 1_000_000_000;
      const display = Number.isInteger(formatted) ? formatted.toString() : formatted.toFixed(1);
      return `${display}B`;
    }
    if (absValue >= 1_000_000) {
      const formatted = absValue / 1_000_000;
      const display = Number.isInteger(formatted) ? formatted.toString() : formatted.toFixed(1);
      return `${display}M`;
    }
    if (absValue >= 1_000) {
      const formatted = absValue / 1_000;
      const display = Number.isInteger(formatted) ? formatted.toString() : formatted.toFixed(1);
      return `${display}K`;
    }

    return absValue.toString();
  };

  const chartData = {
    labels,
    datasets: chartDatasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll create custom legend
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) => {
            return `${context[0].label}`;
          },
          label: (context: TooltipItem<'bar'>) => {
            const y = context.parsed.y ?? 0;
            return `${context.dataset?.label || 'Unknown'}: ${Number(y).toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
        stacked: true,
      },
      y: {
        display: true,
        grid: {
          color: '#e5e7eb',
          drawBorder: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
          padding: 8,
          callback: (value: string | number) => formatYAxisTick(Number(value)),
        },
        border: {
          display: false,
        },
        stacked: true,
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };

  return (
    <div
      className={`bg-white card hover:border-gray-200 border-gray-200 dark:hover:border-[#374151] shadow-sm p-6 ${className}`}
    >
      {/* Header */}
      <div className='flex items-start justify-between mb-4'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>{title}</h3>
          {subtitle && <p className='text-sm text-gray-500 mt-0.5'>{subtitle}</p>}
        </div>
        <div className='flex items-center gap-4'>
          {datasets.length > 0 && (
            <div className='flex items-center gap-3'>
              {datasets.map((ds) => (
                <div key={ds.label} className='flex items-center gap-1.5'>
                  <span
                    className='inline-block w-3 h-3 rounded-sm shrink-0'
                    style={{ backgroundColor: ds.color }}
                  />
                  <span className='text-xs text-gray-500 font-semibold'>{ds.label}</span>
                </div>
              ))}
            </div>
          )}
          {summary && (
            <div className='text-right'>
              <p className='text-sm text-gray-600'>{summary.period}</p>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className='w-full' style={{ height }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default StackedBarChart;
